// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
export function caseBuddyHashString(value) {
  const text = String(value || '');
  let hash = 5381;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) + hash) + text.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

export function escapeRegExp(value = '') {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizeCell(value) {
  return value == null ? '' : String(value).trim();
}

export function rowHasMeaningfulData(row) {
  return Array.isArray(row) && row.some(cell => normalizeCell(cell) !== '');
}

export function safeDateLabel(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '';
  }
}

export function buildTsv(data = []) {
  return (Array.isArray(data) ? data : [])
    .filter(rowHasMeaningfulData)
    .map(row => (Array.isArray(row) ? row : [])
      .map(cell => String(cell ?? '').replace(/\t/g, ' ').replace(/\r?\n/g, ' '))
      .join('\t'))
    .join('\n');
}

export function normalizeComparisonKey(value) {
  return normalizeCell(value).toLowerCase();
}

export function firstNonEmptyValue(...values) {
  for (const value of values) {
    const normalized = normalizeCell(value);
    if (normalized) return normalized;
  }
  return '';
}

export function sleep(ms) {
  return new Promise(resolve => window.setTimeout(resolve, Math.max(0, Number(ms) || 0)));
}

export function abbreviateCaseBuddyHash(value) {
  const text = normalizeCell(value);
  if (!text) return '(blank hash)';
  if (text.length <= 12) return text;
  return `${text.slice(0, 6)}...${text.slice(-4)}`;
}

export function firstNonEmptyEntryValue(entryRows = [], columnIndex) {
  for (const entryRow of entryRows) {
    const value = normalizeCell(entryRow?.row?.[columnIndex]);
    if (value) return value;
  }
  return '';
}

export function padUtcDatePart(value, width = 2) {
  return String(value).padStart(width, '0');
}

export function formatCanonicalUtcDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  return `${date.getUTCFullYear()}-${padUtcDatePart(date.getUTCMonth() + 1)}-${padUtcDatePart(date.getUTCDate())} ${padUtcDatePart(date.getUTCHours())}:${padUtcDatePart(date.getUTCMinutes())}:${padUtcDatePart(date.getUTCSeconds())} UTC`;
}

export function parseCaseUtcDate(value) {
  const text = normalizeCell(value);
  if (!text) {
    return { hasValue: false, isValid: false, timestamp: null, canonical: '', original: '' };
  }

  const explicitMatch = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s*(?:UTC|Z))?$/i);
  if (explicitMatch) {
    const year = Number(explicitMatch[1]);
    const month = Number(explicitMatch[2]);
    const day = Number(explicitMatch[3]);
    const hour = explicitMatch[4] == null ? 0 : Number(explicitMatch[4]);
    const minute = explicitMatch[5] == null ? 0 : Number(explicitMatch[5]);
    const second = explicitMatch[6] == null ? 0 : Number(explicitMatch[6]);
    const timestamp = Date.UTC(year, month - 1, day, hour, minute, second);
    const date = new Date(timestamp);
    const valid = Number.isFinite(timestamp) &&
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day &&
      date.getUTCHours() === hour &&
      date.getUTCMinutes() === minute &&
      date.getUTCSeconds() === second;

    return valid
      ? { hasValue: true, isValid: true, timestamp, canonical: formatCanonicalUtcDate(date), original: text }
      : { hasValue: true, isValid: false, timestamp: null, canonical: '', original: text };
  }

  const normalizedText = text.replace(/\s+UTC$/i, 'Z').replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2})?)Z$/i, '$1T$2Z');
  const timestamp = Date.parse(normalizedText);
  if (!Number.isFinite(timestamp)) {
    return { hasValue: true, isValid: false, timestamp: null, canonical: '', original: text };
  }

  return { hasValue: true, isValid: true, timestamp, canonical: formatCanonicalUtcDate(new Date(timestamp)), original: text };
}

export function normalizeCaseUtcDate(value) {
  const info = parseCaseUtcDate(value);
  return info.isValid ? info.canonical : '';
}
